import { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import { fqsRequest } from '@ctw/shared/api/fqs/client';
import type { InferQueryResult } from '@ctw/shared/api/fqs/helpers';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import { SearchDocument } from '@ctw/shared/types/graphql/__generated__/graphql';
import { QUERY_KEY_RESOURCE_SEARCH } from '@ctw/shared/utils/query-keys';
import type { UseQueryResult } from '@tanstack/react-query';
import type {
  AllergyIntolerance,
  Condition,
  DiagnosticReport,
  DocumentReference,
  MedicationStatement,
  Observation,
} from 'fhir/r4';
import { compact } from 'lodash-es';

export type SearchDocumentQueryResult = InferQueryResult<typeof SearchDocument>;
export type SearchDocumentQuerySearchResult = NonNullable<
  SearchDocumentQueryResult['SearchConnection']
>['edges'][number]['node'];
export type SearchDocumentQueryMatchResult = SearchDocumentQuerySearchResult['matches'][number];

export interface PatientRecordSearchResult
  extends Omit<SearchDocumentQuerySearchResult, 'resource'> {
  allMatches: Array<SearchDocumentQueryMatchResult>;
  resource:
    | DocumentModel
    | AllergyModel
    | ConditionModel
    | MedicationStatementModel
    | DiagnosticReportModel
    | ObservationModel;
}

export function usePatientRecordSearch(
  query: string,
  isExactMatch?: boolean,
): UseQueryResult<Array<PatientRecordSearchResult>> {
  const apiQuery = isExactMatch && query ? `"${query}"` : query;

  return usePatientQuery({
    queryId: QUERY_KEY_RESOURCE_SEARCH,
    queryKey: [query, isExactMatch],
    queryFn: async ({ telemetry, graphqlClient, patient }) => {
      const { data } = await fqsRequest<SearchDocumentQueryResult>(
        telemetry,
        graphqlClient,
        SearchDocument.toString(),
        {
          upid: patient.UPID,
          query: apiQuery,
          first: 500,
        },
      );

      return compact(
        data.SearchConnection?.edges.map((result): PatientRecordSearchResult | undefined => {
          const allMatches: Array<SearchDocumentQueryMatchResult> = [
            ...result.node.matches,
            ...(result.node.relatedResources?.flatMap(
              (relatedResource) => relatedResource.matches,
            ) ?? []),
          ];

          let resource: PatientRecordSearchResult['resource'] | undefined;
          switch (result.node.resourceType) {
            case 'DocumentReference':
              resource = new DocumentModel(result.node.resource as DocumentReference);
              break;
            case 'AllergyIntolerance':
              resource = new AllergyModel(result.node.resource as AllergyIntolerance);
              break;
            case 'Condition':
              resource = new ConditionModel(result.node.resource as Condition);
              break;
            case 'MedicationStatement':
              resource = new MedicationStatementModel(result.node.resource as MedicationStatement);
              break;
            case 'DiagnosticReport':
              resource = new DiagnosticReportModel(result.node.resource as DiagnosticReport);
              break;
            case 'Observation':
              resource = new ObservationModel(result.node.resource as Observation);
              break;
            default:
              // `result.node.resource` is a `GroupedSearchNode` which we can't handle unless we know
              // the type, so ignore it and filter it out
              resource = undefined;
              break;
          }

          return resource === undefined
            ? undefined
            : {
                ...result.node,
                resource,
                allMatches,
              };
        }),
      );
    },
    enabled: !!query,
  });
}
